import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { C } from './cedsStyles';

export default function CEDSPortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [member, setMember] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    async function checkAuth() {
      // getSession reads from localStorage — no network round-trip
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) { navigate('/ceds/login'); return; }

      const { data: m } = await supabase
        .from('ceds_council_members')
        .select('role, is_active, full_name, county')
        .eq('id', session.user.id)
        .maybeSingle();

      if (!m || !m.is_active) { navigate('/ceds/login'); return; }
      setMember(m);
      setChecking(false);
    }
    checkAuth();

    // Redirect to login if session expires while portal is open
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') navigate('/ceds/login');
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  async function handleSignOut() {
    await supabase.auth.signOut();
    window.location.replace('/ceds/login');
  }

  if (checking) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: C.cream, fontFamily: "'Nunito', 'Segoe UI', sans-serif", color: C.muted,
      }}>
        Loading…
      </div>
    );
  }

  const navLinks = [
    { path: '/ceds/portal/discussions', label: '💬  Discussions' },
    { path: '/ceds/portal/documents',   label: '📁  Documents'   },
    { path: '/ceds/portal/worksheets',  label: '📋  Drop Box'    },
    ...(member.role === 'padd_admin'
      ? [{ path: '/ceds/portal/admin', label: '⚙️  Admin' }]
      : []),
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Nunito', 'Segoe UI', sans-serif" }}>
      {/* ── Sidebar ── */}
      <div style={{
        width: 220, flexShrink: 0,
        background: C.navy, display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, bottom: 0, left: 0,
      }}>
        {/* Brand */}
        <div style={{ padding: '24px 20px 16px' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.gold, letterSpacing: '-0.02em' }}>
            PADD
          </div>
          <div style={{
            fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.55)',
            textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 2,
          }}>
            CEDS Strategy Council
          </div>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.1)', margin: '0 20px' }} />

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 0' }}>
          {navLinks.map(link => {
            const active = location.pathname === link.path;
            return (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                  border: 'none',
                  borderLeft: active ? `3px solid ${C.gold}` : '3px solid transparent',
                  color: active ? C.white : 'rgba(255,255,255,0.7)',
                  padding: '10px 20px',
                  fontSize: 14, fontWeight: active ? 600 : 400,
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {link.label}
              </button>
            );
          })}
        </nav>

        {/* User info + sign out */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
            {member.full_name}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
            {member.county} County
          </div>
          <button
            onClick={handleSignOut}
            style={{
              marginTop: 12, background: 'none', border: 'none',
              color: 'rgba(255,255,255,0.45)', fontSize: 12,
              cursor: 'pointer', padding: 0, textDecoration: 'underline',
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={{ marginLeft: 220, flex: 1, background: C.cream, minHeight: '100vh', padding: 32 }}>
        <Outlet />
      </div>
    </div>
  );
}
